import { z, ZodError } from 'zod';
import { maxWorkoutDurationMinutes } from '@client/new/pages/workout/models/workout.domain';
import { LegacyWorkoutFormValues } from '@client/new/pages/workout/models/workoutEdit.model';
import { Control } from 'react-hook-form';
import { last } from 'lodash';

/**
 * Validates if Workout is ready to be published
 * @param {LegacyWorkoutFormValues} values
 * @param {Control<LegacyWorkoutFormValues>} control
 * @return {boolean}
 */
export const validatePublish = (
  values: LegacyWorkoutFormValues,
  control: Control<LegacyWorkoutFormValues>,
): boolean => {
  try {
    publishValidationSchema.parse(values);

    return true;
  } catch (error) {
    const { issues } = error as ZodError;

    issues.map((error) => {
      if (last(error.path) === 0) error.path.pop();
      const field = error.path.join('.') as keyof LegacyWorkoutFormValues;
      control.setError(field, {
        message: error.message,
      });
    });

    console.log({
      message: 'Pre-publish validation errors',
      values,
      errors: issues,
    });

    return false;
  }
};

const simpleExerciseSchema = z.object({
  type: z.literal(2),
  name: z.string().nonempty({ message: 'Title is required' }),
  sets: z
    .array(
      z.object({
        reps: z.tuple([z.coerce.number().gte(1), z.string()]),
      }),
    )
    .optional(),
});

const toMultipleSchemaBase = <T extends number>(type: T) =>
  z.object({
    type: z.literal(type),
    name: z.string().nonempty({ message: 'Title is required' }),
    duration: z.tuple([z.coerce.number().gte(1), z.string()]),
    exercises: z.array(simpleExerciseSchema),
  });

const tabataSchema = z.object({
  type: z.literal(10),
  // name: z.string().nonempty({ message: 'Title is required' }),
  // duration: z.tuple([z.coerce.number().gte(1), z.string()]),
  exercises: z.array(simpleExerciseSchema),
});
const hiitSchema = z.object({
  type: z.literal(11),
  // name: z.string().nonempty({ message: 'Title is required' }),
  // duration: z.tuple([z.coerce.number().gte(1), z.string()]),
  exercises: z.array(simpleExerciseSchema),
});
const circuitSchema = toMultipleSchemaBase(3);

const longFormVideoSchema = z.object({
  type: z.literal(4),
  name: z.string().nonempty({ message: 'Title is required' }),
});

const runningExerciseSchema = z.object({
  type: z.literal(100),
  sets: z
    .array(z.object({ time: z.coerce.number().gte(1) }))
    .nonempty({ message: 'One or more exercises required' }),
});

const publishValidationSchema = z.object({
  name: z.string().nonempty({ message: 'Title is required' }),
  duration: z.coerce
    .number({ invalid_type_error: 'Duration must be defined' })
    .gte(1)
    .lte(maxWorkoutDurationMinutes),
  description: z
    .string()
    .nonempty({ message: 'Description is required when publishing a workout' }),
  programId: z
    .number({
      invalid_type_error: 'Collection is required when publishing a workout',
    })
    .gte(0),
  exercises: z
    .array(
      z.discriminatedUnion('type', [
        simpleExerciseSchema,
        circuitSchema,
        longFormVideoSchema,
        runningExerciseSchema,
        tabataSchema,
        hiitSchema,
      ]),
    )
    .nonempty({ message: 'One or more exercises required' }),
});
